import React from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo } from "../lib/seo";
import { Section, SectionHead, Button } from "../components/ui";
import { FaqAccordion, Yes, No } from "../components/marketing";
import { useReveal } from "../lib/useReveal";

const GROUPS = [
  ["The AI agent", ["Human-like AI sales agent", "Media understanding (voice, photo, video, document)", "One-click optimisation", "Real-time web search", "100k-token memory per contact", "Response-speed and typing-delay control", "Multilingual (95+ languages)", "Human takeover", "Chat playground"]],
  ["Sales mechanics", ["AI appointment booking", "High-ticket sales in DMs", "Outbound campaigns", "Automatic follow-ups", "Comment-to-DM on Instagram and Facebook", "Contact tagging and lists", "Cart and no-show recovery", "Task board"]],
  ["Platform", ["Open REST API (40+ resources, three auth methods)", "Webhooks (~20 event types, HMAC signing, retries)", "Custom Functions", "MCP server", "BYOK (bring your own Anthropic key)", "Advanced Mode", "Team seats", "Unified inbox"]],
];
const MATRIX = [
  ["Voice-note understanding", true, true, true],
  ["Image understanding", true, true, true],
  ["Video & document understanding", false, true, true],
  ["AI appointment booking", true, true, true],
  ["Outbound campaigns", true, true, true],
  ["Custom Functions", false, true, true],
  ["API access & webhooks", true, true, true],
  ["BYOK & Advanced Mode", false, true, true],
  ["Priority support & DPA", false, false, true],
];

export default function Features() {
  const { data: channels } = useQuery({ queryKey: ["channels"], queryFn: () => get("/channels-directory") });
  const { data: faqData } = useQuery({ queryKey: ["faqs"], queryFn: () => get("/faqs") });
  useReveal(channels);
  const faqs = (faqData?.faqs || []).filter((f) => f.category === "Features & automation").slice(0, 6);
  return (
    <>
      <Seo title="Features" path="/features/" description="Every BloomCONNECT feature in one place — the AI agent, seven channels, sales mechanics, and an open platform with API, webhooks and MCP." />
      <Section>
        <SectionHead label="Features" title="Every BloomCONNECT feature, in one place." intro="Every feature exists to do one thing: book the call and close the sale inside the DM." />
      </Section>
      {GROUPS.map((g, gi) => (
        <Section key={g[0]} sunk={gi % 2 === 0}>
          <SectionHead n={`0${gi + 1}`} label={g[0]} title={g[0]} />
          <ul className="mt-8 grid gap-x-8 gap-y-3 md:grid-cols-2 border-t border-line pt-6">
            {g[1].map((f) => <li key={f} className="flex items-start gap-3 reveal-on-scroll"><span className="mt-2 h-px w-4 bg-brand-600 shrink-0" /><span className="text-ink-800">{f}</span></li>)}
          </ul>
          {gi === 0 && <div className="mt-8"><Button to="/features/channels/" variant="secondary" size="md">See all channels</Button></div>}
        </Section>
      ))}
      <Section>
        <SectionHead n="04" label="Availability" title="What's in each plan." />
        <div className="mt-8 overflow-x-auto">
          <table className="w-full border-collapse"><caption className="sr-only">Feature availability by plan</caption>
            <thead><tr className="border-b border-line"><th scope="col" className="py-3 px-3 text-left font-mono text-2xs uppercase tracking-eyebrow text-ink-400">Feature</th>{["Growth","Pro","Enterprise"].map(h=><th key={h} scope="col" className="py-3 px-3 text-center font-mono text-2xs uppercase tracking-eyebrow text-ink-600">{h}</th>)}</tr></thead>
            <tbody>{MATRIX.map(r=>(<tr key={r[0]} className="border-b border-line"><td className="py-2.5 px-3 text-sm text-ink-800">{r[0]}</td>{r.slice(1).map((v,i)=><td key={i} className="py-2.5 px-3 text-center">{v?<Yes/>:<No/>}</td>)}</tr>))}</tbody>
          </table>
        </div>
      </Section>
      <Section sunk>
        <SectionHead n="05" label="FAQ" title="Feature questions." />
        <div className="mt-8 max-w-3xl">{faqs.length>0 && <FaqAccordion items={faqs} />}</div>
      </Section>
    </>
  );
}
